import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import './App.css';
import WiresBackground from './WiresBackground';

// Use relative URL in production, localhost in development
const API_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:8000' : '');

const circulatedGrades = ['6', '15', '45'];
const mintStateGrades = ['60', '61', '62', '63', '64', '65', '66', '67'];

// Test images available in public/test-coins/
const testImages = [
  { id: 'g-6', label: 'G-6', file: 'g-6.jpg' },
  { id: 'f-15', label: 'F-15', file: 'f-15.jpg' },
  { id: 'xf-45', label: 'EF-45', file: 'xf-45.jpg' },
  { id: '60-1', label: 'MS-60', file: '60-1.jpg' },
  { id: '61', label: 'MS-61', file: '61.jpg' },
  { id: '62-1', label: 'MS-62', file: '62-1.jpg' },
  { id: '63', label: 'MS-63', file: '63.jpg' },
  { id: 'ms63-slab', label: 'MS-63 (NGC)', file: 'ms63-slab-ngc.jpg' },
  { id: '64', label: 'MS-64', file: '64.jpg' },
  { id: 'ms64-slab', label: 'MS-64 (PCGS)', file: 'ms64-slab.jpg' },
  { id: '65-2', label: 'MS-65', file: '65-2.jpg' },
  { id: 'ms66-1', label: 'MS-66', file: 'ms66-1.jpg' },
  { id: 'ms66-slab-cac', label: 'MS-66 (PCGS CAC)', file: 'ms66-slab-cac.jpg' },
  { id: 'ms66-slab', label: 'MS-66 (NGC)', file: 'ms66-slab-ngc.jpg' },
  { id: 'ms67-1', label: 'MS-67', file: 'ms67-1.jpg' },
];

function App() {
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
      setResult(null);
      setError(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    maxFiles: 1,
  });

  const handleGrade = async () => {
    if (!image) return;

    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', image);

    try {
      const response = await axios.post(`${API_URL}/grade`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to grade coin. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setImage(null);
    setImagePreview(null);
    setResult(null);
    setError(null);
  };

  const loadTestImage = async (testImage) => {
    if (!testImage) return;
    
    setResult(null);
    setError(null);
    
    try {
      const response = await fetch(`/test-coins/${testImage.file}`);
      const blob = await response.blob();
      const file = new File([blob], testImage.file, { type: 'image/jpeg' });
      
      setImage(file);
      setImagePreview(URL.createObjectURL(blob));
    } catch (err) {
      setError('Failed to load test image');
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 70) return 'var(--success)';
    if (confidence >= 50) return 'var(--warning)';
    return 'var(--error)';
  };

  return (
    <div className="app">
      <WiresBackground />
      {/* Header */}
      <header className="header">
        <div className="logo">
          <h1>GradeCoin.io</h1>
        </div>
        <p className="tagline">AI-Powered Morgan Dollar Grader</p>
        <div className="grade-range">G-6 → MS-67</div>
      </header>

      <main className="main">
        {/* Upload Section */}
        <section className="upload-section">
          <div
            {...getRootProps()}
            className={`dropzone ${isDragActive ? 'active' : ''} ${imagePreview ? 'has-image' : ''}`}
          >
            <input {...getInputProps()} />
            
            {imagePreview ? (
              <div className="preview-container">
                {result?.processed_image && (
                  <span className="preview-caption">Your upload</span>
                )}
                <img src={imagePreview} alt="Coin preview" className="preview-image" />
                {result?.is_slabbed && (
                  <span className="slab-badge">Slabbed</span>
                )}
              </div>
            ) : (
              <div className="dropzone-content">
                <div className="upload-icon">📤</div>
                <h3>Drop your coin image here</h3>
                <p>or click to browse</p>
                <span className="supported">Supports: JPG, PNG, WebP</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="actions">
            {imagePreview && !result && (
              <button 
                className="btn btn-primary" 
                onClick={handleGrade}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Analyzing... (may take a few seconds)
                  </>
                ) : (
                  <>🔍 Grade Coin</>
                )}
              </button>
            )}
            
            {(imagePreview || result) && (
              <button className="btn btn-secondary" onClick={resetForm}>
                ↻ New Image
              </button>
            )}
          </div>

          {result?.processed_image && (
            <div className="processed-crop-panel">
              <div className="processed-crop-heading">As analyzed for AI</div>
              <p className="processed-crop-hint">
                Cropped coin with background masked (same input the model uses before resize to 384×384).
              </p>
              <div className="processed-crop-frame">
                <img
                  src={`data:${result.processed_image.mime};base64,${result.processed_image.base64}`}
                  alt="Processed crop sent to the grader"
                  className="processed-crop-image"
                />
              </div>
              <div className="processed-crop-meta">
                {result.processed_image.width} × {result.processed_image.height} px
              </div>
            </div>
          )}

          {/* Test Image Selector */}
          <div className="test-selector">
            <label>Try a sample coin:</label>
            <select 
              onChange={(e) => {
                const selected = testImages.find(t => t.id === e.target.value);
                if (selected) loadTestImage(selected);
              }}
              defaultValue=""
            >
              <option value="" disabled>Select a test image...</option>
              <optgroup label="Circulated">
                {testImages.filter(t => ['g-6', 'f-15', 'xf-45'].includes(t.id)).map(t => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </optgroup>
              <optgroup label="Mint State">
                {testImages.filter(t => !['g-6', 'f-15', 'xf-45'].includes(t.id)).map(t => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </optgroup>
            </select>
          </div>
        </section>

        {/* Error Display */}
        {error && (
          <div className="error-card">
            <span>⚠️</span>
            <p>{error}</p>
          </div>
        )}
        
        {/* Invalid Coin Warning */}
        {result && !result.is_valid_coin && (
          <div className="warning-card">
            <span>🚫</span>
            <div>
              <p className="warning-title">Not a Morgan Dollar</p>
              <p className="warning-text">This image doesn't match Morgan Dollar patterns. Please upload a clear image of a Morgan Dollar coin.</p>
            </div>
          </div>
        )}

        {/* Results Section */}
        {result && result.is_valid_coin && (
          <section className="results-section">
            {/* Main Grade Card */}
            <div className="grade-card">
              <div className="grade-header">
                <span className="grade-value">{result.grade_info.name}</span>
                <span className="grade-title">{result.grade_info.title}</span>
              </div>
              
              <p className="grade-desc">{result.grade_info.desc}</p>
              
              {/* Confidence Bar */}
              <div className="confidence-section">
                <div className="confidence-header">
                  <span>Confidence</span>
                  <span 
                    className="confidence-value"
                    style={{ color: getConfidenceColor(result.confidence) }}
                  >
                    {Math.round(result.confidence)}%
                  </span>
                </div>
                <div className="confidence-bar">
                  <div 
                    className="confidence-fill"
                    style={{ 
                      width: `${Math.min(result.confidence, 100)}%`,
                      background: getConfidenceColor(result.confidence)
                    }}
                  ></div>
                </div>
              </div>

              {result.is_circulated && (
                <div className="circulated-badge">
                  Circulated
                </div>
              )}
              
              {!result.is_circulated && (
                <div className="mint-state-badge">
                  Mint State
                </div>
              )}
              
            </div>

            {/* Grade Scale */}
            <div className="grade-scale-card">
              <h3>Grade Scale</h3>
              
              {/* Circulated Grades */}
              <div className="scale-section">
                <span className="scale-label">Circulated</span>
                <div className="grade-scale circulated">
                  {circulatedGrades.map((g) => {
                    const gradeNames = {'6': 'G-6', '15': 'F-15', '45': 'EF-45'};
                    const isActive = result.grade_info.name === gradeNames[g];
                    return (
                      <div key={g} className={`grade-item ${isActive ? 'active circulated' : ''}`}>
                        <span>{gradeNames[g]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Mint State Grades */}
              <div className="scale-section">
                <span className="scale-label">Mint State</span>
                <div className="grade-scale mint-state">
                  {mintStateGrades.map((g) => (
                    <div 
                      key={g}
                      className={`grade-item ${result.grade_info.name === `MS-${g}` ? 'active' : ''}`}
                    >
                      <span>{g}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Grade Analysis - Combined probabilities without revealing model details */}
            {result.probabilities && (
              <div className={`probabilities-card ${result.is_circulated ? 'circulated' : ''}`}>
                <h3>Grade Analysis</h3>
                <p className="prob-subtitle">AI confidence distribution across possible grades</p>
                <div className="prob-list">
                  {Object.entries(
                    // Use specialist probabilities if available, otherwise use base probabilities
                    result.specialist_probabilities || result.probabilities
                  )
                    .sort(([,a], [,b]) => b - a)
                    .map(([grade, prob]) => (
                      <div 
                        key={grade} 
                        className={`prob-item ${grade === result.grade_info.name ? 'active' : ''}`}
                      >
                        <span className="prob-grade">{grade}</span>
                        <div className="prob-bar-container">
                          <div 
                            className="prob-bar"
                            style={{ width: `${Math.min(prob, 100)}%` }}
                          ></div>
                        </div>
                        <span className="prob-value">{Math.round(prob)}%</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="footer">
        <p>Powered by Deep Learning</p>
        <p className="footer-credit">Created by Joe Johnson</p>
      </footer>
    </div>
  );
}

export default App;
